import { useCollections } from '@dittolive/react-ditto'
import { useQuery } from '@tanstack/react-query'
import { useDittoAppConnection } from 'components/dittoAppConnection'
import { useRPCHook } from 'components/hooks/rpc'
import { useEffect, useState } from 'react'

type Props = {
  appId: string
} & Pick<Parameters<typeof useCollections>[0], 'sort'>

export function useAppCollections({
  appId,
  sort = {
    propertyPath: 'name',
    direction: 'ascending',
  },
}: Props) {
  const { cloudDomain } = useDittoAppConnection(appId, false)
  const { rpcAccessToken, getCollectionDocumentCount } = useRPCHook({
    appId,
    cloudUrl: cloudDomain?.url,
  })

  const { documents, collectionsEvent } = useCollections({
    path: appId,
    sort,
  })

  const { data: rpcCount, isLoading: isRpcCountLoading } = useQuery({
    queryKey: ['collections', appId],
    queryFn: () => getCollectionDocumentCount('__collections'),
    enabled: !!rpcAccessToken,
    retry: (attempts) => attempts < 3,
  })

  const [isWaitingForInitial, setIsWaitingForInitial] = useState(true)

  useEffect(() => {
    if (collectionsEvent?.isInitial) {
      setIsWaitingForInitial(false)
    }
  }, [collectionsEvent])

  const isLoading =
    isWaitingForInitial || isRpcCountLoading || rpcCount === undefined
  const isForcedLoadingState =
    isLoading || (!documents.length && (rpcCount || 0) > 0)

  return {
    documents,
    isLoading: isForcedLoadingState,
  }
}
